import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ScanEye, Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import { api } from '@/lib/api';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Ignore legacy provider details/reset URLs during rolling upgrades too.
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
      toast.success('Request received');
    } catch (_err) {
      toast.error('We couldn’t process that request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col hero-mist">
      <header className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 lg:px-8 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-forgot-home-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><ScanEye className="h-5 w-5" /></div>
          <div className="font-display text-base font-semibold">GEOrush</div>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border card-elevated bg-card">
          <div className="p-6 sm:p-8">
            {!sent ? (
              <>
                <h1 className="font-display text-2xl font-semibold tracking-tight">Forgot your password?</h1>
                <p className="mt-1 text-sm text-muted-foreground">Enter the email on your account and we’ll send you a link to reset it.</p>
                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-forgot-email-input" placeholder="you@brand.com" autoFocus />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="auth-forgot-submit-button">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send reset link
                  </Button>
                </form>
                <div className="mt-6 text-center text-sm text-muted-foreground">
                  <Link to="/login" className="inline-flex items-center gap-1 text-[hsl(var(--accent))] font-medium hover:underline" data-testid="auth-forgot-back-to-login-link">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <MailCheck className="h-6 w-6" />
                </div>
                <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">Check your inbox</h1>
                <p className="mt-2 text-sm text-muted-foreground" data-testid="auth-forgot-sent-detail" role="status">If an account exists for that email, you will receive a password reset link shortly. Check your spam folder too.</p>
                <div className="mt-6 flex flex-col gap-2">
                  <Button variant="outline" onClick={() => { setSent(false); setEmail(''); }} data-testid="auth-forgot-send-again-button">Send another link</Button>
                  <Button variant="ghost" onClick={() => navigate('/login')} data-testid="auth-forgot-back-to-login-button">Back to sign in</Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
